import { useState, useEffect } from 'react';
import { useSessions, type Session } from '@/lib/use-sessions';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  MessageSquare,
  Trash2,
  Loader2,
  Archive,
} from 'lucide-react';

interface SessionHistoryListProps {
  className?: string;
  workspaceId: string | null;
  currentSessionId?: string;
  visible?: boolean;
  onSessionSelect: (sessionId: string) => void;
}

export function SessionHistoryList({
  className,
  workspaceId,
  currentSessionId,
  visible,
  onSessionSelect,
}: SessionHistoryListProps) {
  const { sessions, loading, loadSessions, deleteSession } = useSessions(workspaceId, true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Refresh sessions when sidebar becomes visible
  useEffect(() => {
    if (visible && workspaceId) {
      loadSessions();
    }
  }, [visible, workspaceId, loadSessions]);

  const handleDelete = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    if (!confirm('Delete this chat? This cannot be undone.')) return;

    setDeletingId(sessionId);
    try {
      await deleteSession(sessionId);
    } catch (err) {
      console.error('Failed to delete session:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const isArchived = (s: Session) => s.executionStatus === 'ARCHIVED';

  return (
    <ScrollArea className={cn('min-h-0', className)}>
      <div className="p-2 space-y-0.5">
        {loading && sessions.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && sessions.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="size-8 mx-auto mb-2 opacity-50" />
            <p className="text-xs">No chats yet</p>
          </div>
        )}

        {sessions.map((session) => (
          <SessionItem
            key={session.id}
            session={session}
            isActive={session.id === currentSessionId}
            isDeleting={deletingId === session.id}
            isArchived={isArchived(session)}
            onSelect={() => onSessionSelect(session.id)}
            onDelete={(e) => handleDelete(e, session.id)}
          />
        ))}
      </div>
    </ScrollArea>
  );
}

function SessionItem({
  session,
  isActive,
  isDeleting,
  isArchived,
  onSelect,
  onDelete,
}: {
  session: Session;
  isActive: boolean;
  isDeleting: boolean;
  isArchived: boolean;
  onSelect: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const [showDelete, setShowDelete] = useState(false);
  const title = session.displayName || session.assistant?.name || 'Untitled Chat';

  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
      className={`w-full group flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
        isActive
          ? 'bg-primary/10 text-primary font-medium'
          : isArchived
            ? 'hover:bg-accent/50 text-muted-foreground'
            : 'hover:bg-accent/50 text-foreground'
      }`}
    >
      {isArchived ? (
        <Archive className="size-3 shrink-0 text-muted-foreground" />
      ) : (
        <MessageSquare className={`size-3 shrink-0 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
      )}
      <span className={`flex-1 text-left truncate ${isArchived ? 'italic' : ''}`}>{title}</span>
      {showDelete && !isDeleting && (
        <button
          onClick={onDelete}
          className="shrink-0 p-0.5 rounded hover:bg-destructive/20 hover:text-destructive transition-colors"
          title="Delete chat"
        >
          <Trash2 className="size-3" />
        </button>
      )}
      {isDeleting && <Loader2 className="size-3 animate-spin shrink-0" />}
    </button>
  );
}
